import { Component } from '@angular/core';
import {
  SituViewWebApiClient,
  FileVaultReference,
  FileVaultCategory,
  FileVaultReferencesOrderField,
  GetFileVaultReferencesRequest,
  GetZippedFilesRequest,
  CompanyProductIdPair,
  FileVaultUploadRequest,
  FileVaultUploadCommitRequest,
  FileVaultSubjectType,
  RemoveFileVaultReferencesRequest,
  AddFileVaultReferencesRequest,
  CompanySubjectPair
} from 'situview-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProductSelectorDialogComponent, ProductSelectorDialogData, ProductSelectorResultData } from 'src/app/components';
import { AuthService, BreadcrumbService, StateService, Utils, ConfirmDialogService, FileVaultService, FileVaultUpload } from 'src/app/services';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as FileSaver from 'file-saver';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

async function runParallel<T>(items: T[], concurrency: number, fn: (item: T) => Promise<void>): Promise<void> {
  const executing: Promise<void>[] = [];
  for (const item of items) {
    const p = fn(item).then(() => { executing.splice(executing.indexOf(p), 1); });
    executing.push(p);
    if (executing.length >= concurrency) {
      await Promise.race(executing);
    }
  }
  await Promise.all(executing);
}

@Component({
  selector: 'app-file-vault-browser',
  templateUrl: './file-vault-browser.component.html',
  styleUrls: ['./file-vault-browser.component.scss']
})
export class FileVaultBrowserComponent extends SituBaseComponent {
  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100]
  };

  dataSource: SelectableFileVaultReference[] = [];
  searchTerm: string = '';
  categoryFilter: FileVaultCategory | undefined = undefined;
  fileVaultCategories = FileVaultCategory;
  authorizedCategories: FileVaultCategory[] = [];
  orderBy: FileVaultReferencesOrderField = FileVaultReferencesOrderField.FileName;
  orderDescending = false;
  orderSelection: string = `${FileVaultReferencesOrderField.FileName}|asc`;

  // Upload state
  uploadCategory: FileVaultCategory | undefined = undefined;
  newFiles: NewFileDetails[] = [];
  uploading = false;
  validFileExtensions: string[] = [];

  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  private searchTermChangedSubject = new Subject<void>();

  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private situViewClient: SituViewWebApiClient,
    private confirmDialogService: ConfirmDialogService,
    private fileVaultService: FileVaultService,
    private dialog: MatDialog
  ) {
    super(authService, stateService);

    this._subscriptions.push(
      this.searchTermChangedSubject.pipe(debounceTime(600)).subscribe(() => {
        this.viewControls.pageIndex = 0;
        this.loadFiles();
      })
    );
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.loadAuthorizedCategories();
    this.loadFiles();
  }

  private async loadAuthorizedCategories() {
    try {
      this.authorizedCategories = await this.situViewClient.getAuthorizedFileVaultCategories();
    } catch {
      this.authorizedCategories = [];
    }
  }

  protected onSelectedCompanyChange(): void {
    this.viewControls.pageIndex = 0;
    this.loadFiles();
  }

  debounceSearchTermChanged() {
    this.searchTermChangedSubject.next();
  }

  onCategoryChanged() {
    this.viewControls.pageIndex = 0;
    this.loadFiles();
  }

  orderChanged() {
    var split = this.orderSelection.split('|');
    this.orderBy = parseInt(split[0]) as FileVaultReferencesOrderField;
    this.orderDescending = split[1] === 'desc';
    this.viewControls.pageIndex = 0;
    this.loadFiles();
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;
    this.loadFiles();
  }

  async loadFiles() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      var request = new GetFileVaultReferencesRequest();
      request.companyId = this.selectedCompany?.id;
      request.category = this.categoryFilter ?? FileVaultCategory.NotSet;
      request.fileNameFilter = this.searchTerm;
      request.pageNumber = this.viewControls.pageIndex + 1;
      request.pageSize = this.viewControls.pageSize;
      request.orderBy = this.orderBy;
      request.orderDescending = this.orderDescending;

      var response = await this.situViewClient.getFileVaultReferences(request);

      if (!response.success) {
        this.showMessage('There was an issue loading file vault files. Please refresh the page to try again.');
        return;
      }

      this.dataSource = (response.items ?? []) as SelectableFileVaultReference[];
      this.viewControls.totalItems = response.totalItems ?? 0;
    } finally {
      this.loading = false;
    }
  }

  formatFileSize(bytes: number | undefined): string {
    if (bytes == null || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + ' ' + units[i];
  }

  getCategoryLabel(category: FileVaultCategory | undefined): string {
    switch (category) {
      case FileVaultCategory.ReferenceMaterial: return 'Reference Material';
      case FileVaultCategory.SourceMaterial: return 'Source Material';
      case FileVaultCategory.Assets: return 'Assets';
      case FileVaultCategory.MarketingMaterial: return 'Marketing Material';
      case FileVaultCategory.Logo: return 'Logo';
      default: return 'Unknown';
    }
  }

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }

  timeAgoString(date?: Date): string {
    return Utils.timeAgoString(date);
  }

  hasBeenReplaced(file: FileVaultReference): boolean {
    return this.fileVaultService.hasBeenReplaced(file);
  }

  // Upload methods

  async onUploadCategoryChanged() {
    if (this.uploadCategory) {
      this.validFileExtensions = await this.situViewClient.getValidFileExtensions(this.uploadCategory);
    } else {
      this.validFileExtensions = [];
    }
  }

  buildValidFileTypesString(): string {
    if (this.validFileExtensions.length === 0) {
      return "";
    }
    return `${this.validFileExtensions.join(", ")}.`;
  }

  isValidExtension(file: NewFileDetails): boolean {
    if (this.validFileExtensions.length === 0) return true;
    return this.validFileExtensions.some(x => x === file.extension.toLowerCase());
  }

  onAddNewFileClick() {
    document.getElementById("upload-file-input")?.click();
  }

  onFileChosen(event: any) {
    var files = [...event.target.files] as File[];

    var newFileDetailsCollection: NewFileDetails[] = [];
    for (let file of files) {
      var newFileDetails = this.addToNewFileList(file);
      newFileDetailsCollection.push(newFileDetails);
    }

    newFileDetailsCollection.forEach(async x => await this.calculateFileHash(x));
  }

  dropHandler(ev: DragEvent) {
    ev.preventDefault();

    if (!this.uploadCategory) {
      this.showMessage('Please select an upload category first.');
      return;
    }

    if (!ev.dataTransfer || !ev.dataTransfer.items) {
      return;
    }

    var newFileDetailsCollection: NewFileDetails[] = [];
    var items = ev.dataTransfer.items;
    for (let item of items) {
      if (item.kind === "file") {
        var file = item.getAsFile();
        if (!file) {
          return;
        }

        var newFileDetails = this.addToNewFileList(file);
        newFileDetailsCollection.push(newFileDetails);
      }
    }

    newFileDetailsCollection.forEach(async x => await this.calculateFileHash(x));
  }

  addToNewFileList(file: File): NewFileDetails {
    this.newFiles = this.newFiles.filter(x => x.name !== file.name);

    var newFileDetails = new NewFileDetails(file, this.uploadCategory!);
    this.newFiles.push(newFileDetails);

    return newFileDetails;
  }

  async calculateFileHash(newFileDetails: NewFileDetails) {
    newFileDetails.fileHash = await this.fileVaultService.computeFileMd5Hex(newFileDetails.file);
  }

  async uploadFile(fileDetails: NewFileDetails, refreshList = true, allowDuplicate = false) {
    if (refreshList) this.uploading = true;

    try {
      // A committed file must have a hash; ensure it's ready in case upload beat the async hashing.
      if (!fileDetails.fileHash) {
        await this.calculateFileHash(fileDetails);
      }

      var fileVaultUploadRequest = new FileVaultUploadRequest();
      fileVaultUploadRequest.companyId = this.selectedCompany?.id;
      fileVaultUploadRequest.subjectId = EMPTY_GUID;
      fileVaultUploadRequest.fileName = fileDetails.name;
      fileVaultUploadRequest.category = fileDetails.category;
      fileVaultUploadRequest.fileHash = fileDetails.fileHash;
      fileVaultUploadRequest.allowDuplicate = allowDuplicate;

      var requestUploadResponse = await this.situViewClient.requestFileVaultUpload(fileVaultUploadRequest);

      if (!requestUploadResponse.success) {
        var msg = requestUploadResponse.errorMessages?.find(_ => true);
        throw new Error(msg ? `Failed to request file upload: ${msg}` : "Failed to request file upload.");
      }

      if (requestUploadResponse.fileAlreadyInFileVault) {
        this.confirmDialogService.open({
          title: 'File Already Exists',
          message: `A file identical to '${fileDetails.name}' has already been uploaded. Would you like to upload it as a new file anyway?`,
          cancelText: 'Cancel',
          confirmText: 'Upload As New File',
          actionIcon: 'upload',
          actionColour: 'accent'
        } as ConfirmDialogData);

        var confirmed = await this.confirmDialogService.confirmed().toPromise();
        if (confirmed) {
          await this.uploadFile(fileDetails, refreshList, true);
        } else {
          this.newFiles = this.newFiles.filter(x => x !== fileDetails);
        }
        return;
      }

      fileDetails.referenceId = requestUploadResponse.fileVaultReferenceId;
      fileDetails.uploadSession = this.fileVaultService.createUpload(
        fileDetails.file,
        requestUploadResponse.uploadUrl!,
        () => this.fileVaultService.refreshUploadUrl(this.selectedCompany?.id, fileDetails.referenceId!, fileDetails.category),
      );

      await this.transferAndCommit(fileDetails, refreshList);
    } catch (e: any) {
      this.showMessage(e.message);
    } finally {
      if (refreshList) this.uploading = false;
    }
  }

  // Uploads the blob and commits it, updating per-file progress. On failure the file is
  // flagged so the user can retry — the upload session re-sends only the parts that failed.
  private async transferAndCommit(fileDetails: NewFileDetails, refreshList: boolean) {
    fileDetails.uploadFailed = false;
    fileDetails.uploading = true;
    fileDetails.progress = 0;

    try {
      await fileDetails.uploadSession!.upload(p => fileDetails.progress = p);
    } catch (e: any) {
      fileDetails.uploading = false;
      fileDetails.uploadFailed = true;
      this.showMessage(e.message ?? "Upload failed. Please retry.");
      return;
    }

    var fileVaultUploadCommitRequest = new FileVaultUploadCommitRequest();
    fileVaultUploadCommitRequest.companyId = this.selectedCompany?.id;
    fileVaultUploadCommitRequest.fileVaultReferenceId = fileDetails.referenceId;
    fileVaultUploadCommitRequest.category = fileDetails.category;
    var uploadCommitResponse = await this.situViewClient.commitFileVaultUpload(fileVaultUploadCommitRequest);

    fileDetails.uploading = false;

    if (!uploadCommitResponse.success) {
      fileDetails.uploadFailed = true;
      this.showMessage(uploadCommitResponse.hashMismatch
        ? "The uploaded file failed its integrity check. Please retry."
        : "Failed to commit file.");
      return;
    }

    this.newFiles = this.newFiles.filter(x => x !== fileDetails);

    if (refreshList) {
      await this.loadFiles();
    }
  }

  async retryUpload(fileDetails: NewFileDetails) {
    // Without a session (e.g. the request itself failed) start the upload from scratch.
    if (!fileDetails.uploadSession || !fileDetails.referenceId) {
      await this.uploadFile(fileDetails, true, true);
      return;
    }

    this.uploading = true;
    try {
      await this.transferAndCommit(fileDetails, true);
    } finally {
      this.uploading = false;
    }
  }

  async uploadAllNewFiles() {
    this.uploading = true;
    var filesToUpload = this.newFiles.filter(f => this.isValidExtension(f) && f.editingName === undefined);
    try {
      await runParallel(filesToUpload, 3, (file) => this.uploadFile(file, false));
    } finally {
      this.uploading = false;
      await this.loadFiles();
    }
  }

  removeNewFile(fileDetails: NewFileDetails) {
    this.newFiles = this.newFiles.filter(x => x !== fileDetails);
  }

  // Selection methods

  hasSelected(): boolean {
    return this.dataSource.some(x => x.isSelected);
  }

  hasSelectedAll(): boolean {
    return this.dataSource.length > 0 && this.dataSource.every(x => x.isSelected);
  }

  selectAll(select: boolean) {
    this.dataSource.forEach(x => x.isSelected = select);
  }

  getSelected(): SelectableFileVaultReference[] {
    return this.dataSource.filter(x => x.isSelected);
  }

  // Linking

  linkFileToProducts(file: FileVaultReference) {
    this.linkFilesToProducts([file]);
  }

  linkSelectedToProducts() {
    this.linkFilesToProducts(this.getSelected());
  }

  linkFilesToProducts(files: FileVaultReference[]) {
    if (files.length === 0) {
      return;
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    var dialogData = new ProductSelectorDialogData();
    dialogData.companyId = this.selectedCompany?.id;
    dialogData.multiSelect = true;
    dialogData.tagSelectionEnabled = false;

    this.productSelectorDialogRef = this.dialog.open(ProductSelectorDialogComponent, {
      width: `${Math.round((70 / 100) * screenWidth)}px`,
      height: `${Math.round((90 / 100) * screenHeight)}px`,
      data: dialogData,
    });

    this._subscriptions.push(this.productSelectorDialogRef.afterClosed()
      .subscribe(async (data: ProductSelectorResultData) => {
        if (!data?.products?.length) {
          return;
        }

        var subjects = data.products.map(x => {
          var companySubjectPair = new CompanySubjectPair();
          companySubjectPair.companyId = x.companyId;
          companySubjectPair.subjectId = x.id;
          return companySubjectPair;
        });

        this.loading = true;
        var linked = 0;
        var failed = false;

        try {
          // One request per category: the ids are resolved within a single category, so a mixed
          // selection has to be split or the files in the other categories are silently skipped.
          var categories = Array.from(new Set(files.map(x => x.category!)));
          for (let category of categories) {
            var request = new AddFileVaultReferencesRequest();
            request.category = category;
            request.fileVaultReferenceIds = files.filter(x => x.category === category).map(x => x.id!);
            request.subjects = subjects;

            var response = await this.situViewClient.addFileVaultReferences(request);
            if (response.success) {
              linked += response.fileVaultReferences?.length ?? 0;
            } else {
              failed = true;
            }
          }
        } finally {
          this.loading = false;
        }

        this.showMessage(failed
          ? 'Failed to link some files to products'
          : `Created ${linked} ${this.pluralize('link', linked)}`);

        await this.loadFiles();
      }));
  }

  // File actions

  async downloadFile(file: FileVaultReference) {
    try {
      var fileResponse = await this.situViewClient.getFile(this.selectedCompany?.id, file.id, file.category);
      FileSaver.saveAs(fileResponse.data, file.fileName);
    } catch (e: any) {
      this.showMessage('Failed to download file.');
    }
  }

  deleteFile(file: FileVaultReference) {
    this.confirmDialogService.open({
      title: 'Delete File',
      message: `Are you sure you wish to permanently delete '${file.fileName}' from the vault?`,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe(async confirmed => {
      if (confirmed) {
        var request = new RemoveFileVaultReferencesRequest();
        request.fileVaultReferenceId = file.id;
        request.category = file.category;
        request.force = true;
        request.deleteFromVault = true;

        var companySubjectPair = new CompanySubjectPair();
        companySubjectPair.companyId = this.selectedCompany?.id;
        companySubjectPair.subjectId = EMPTY_GUID;
        request.subjects = [companySubjectPair];

        var response = await this.situViewClient.removeFileVaultReferences(request);
        if (!response.success) {
          this.showMessage('Failed to delete file.');
        } else if (!response.removedCount) {
          this.showMessage(`Cannot delete '${file.fileName}' — it is still linked to ${file.subjectCount} ${this.pluralize('item', file.subjectCount ?? 0)}.`);
        } else {
          this.showMessage(`Deleted '${file.fileName}'.`);
          await this.loadFiles();
        }
      }
    });
  }

  // Bulk actions

  async downloadSelected() {
    for (let file of this.getSelected()) {
      await this.downloadFile(file);
    }
  }

  async downloadSelectedZip() {
    var selectedFiles = this.getSelected();
    if (selectedFiles.length === 0) return;

    try {
      var request = new GetZippedFilesRequest();
      request.fileVaultReferences = selectedFiles.map(x => {
        const pair = new CompanyProductIdPair();
        pair.companyId = this.selectedCompany?.id!;
        pair.fileId = x.id!;
        pair.category = x.category;
        return pair;
      });
      var fileResponse = await this.situViewClient.getZippedFiles(request);

      var fileName = selectedFiles.length === 1 ? selectedFiles[0].fileName! : 'file-vault-files';
      FileSaver.saveAs(fileResponse.data, `${fileName}.zip`);
    } catch (e: any) {
      this.showMessage('Failed to download zipped files.');
    }
  }

  deleteSelected() {
    var selectedFiles = this.getSelected();
    if (selectedFiles.length === 0) return;

    var deletable = selectedFiles.filter(x => !x.subjectCount || x.subjectCount === 0);
    var skippedCount = selectedFiles.length - deletable.length;

    if (deletable.length === 0) {
      this.showMessage('All selected files are linked to items and cannot be deleted.');
      return;
    }

    var message = `Are you sure you wish to permanently delete ${deletable.length} ${this.pluralize('file', deletable.length)} from the vault?`;
    if (skippedCount > 0) {
      message += ` ${skippedCount} linked ${this.pluralize('file', skippedCount)} will be skipped.`;
    }

    this.confirmDialogService.open({
      title: 'Delete Selected Files',
      message: message,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe(async confirmed => {
      if (confirmed) {
        await runParallel(deletable, 3, async (file) => {
          var request = new RemoveFileVaultReferencesRequest();
          request.fileVaultReferenceId = file.id;
          request.category = file.category;
          request.force = true;
          request.deleteFromVault = true;

          var companySubjectPair = new CompanySubjectPair();
          companySubjectPair.companyId = this.selectedCompany?.id;
          companySubjectPair.subjectId = EMPTY_GUID;
          request.subjects = [companySubjectPair];

          await this.situViewClient.removeFileVaultReferences(request);
        });
        var msg = `Deleted ${deletable.length} ${this.pluralize('file', deletable.length)}.`;
        if (skippedCount > 0) {
          msg += ` Skipped ${skippedCount} linked ${this.pluralize('file', skippedCount)}.`;
        }
        this.showMessage(msg);
        await this.loadFiles();
      }
    });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class SelectableFileVaultReference extends FileVaultReference {
  isSelected = false;
}

class NewFileDetails {
  file: File;
  category: FileVaultCategory;

  isSelected = true;

  nameWithoutExtension = "";
  extension = "";

  fileHash?: string = undefined;
  editingName?: string = undefined;

  // Upload state (progress bar / retry).
  progress = 0;
  uploading = false;
  uploadFailed = false;
  referenceId?: string = undefined;
  uploadSession?: FileVaultUpload = undefined;

  public get name(): string {
    return `${this.nameWithoutExtension}.${this.extension}`
  }
  public set name(value: string) {
    var index = value.lastIndexOf('.');
    this.nameWithoutExtension = value.slice(0, index);
    this.nameWithoutExtension = this.sanitizeFilename(this.nameWithoutExtension);
    this.extension = value.slice(index + 1);
  }

  constructor(file: File, category: FileVaultCategory) {
    this.file = file;
    this.category = category;
    this.name = file.name;
  }

  sanitizeFilename(filename: string): string {
    var maxFileLenth = 254 - this.nameWithoutExtension.length;
    if (filename.length > maxFileLenth) {
      filename = filename.slice(0, maxFileLenth);
    }

    return filename.replace(/[/\\?%*:|"<>]/g, '_');
  }

  enableNameEdit() {
    this.editingName = this.nameWithoutExtension;
  }

  saveNameEdit() {
    this.nameWithoutExtension = this.sanitizeFilename(this.editingName!);
    this.editingName = undefined;
  }
}
